import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { toFivePointScore } from '../../../../../../pure-js/libs/Common'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { getPsychologicalSafetyQuestionLabel } from '../../../../shared/utils/variableTranslationHelpers'

const DEFAULT_TEAM_LABELS = [
  'teamReflectionQuestionOne',
  'teamReflectionQuestionTwo',
  'teamReflectionQuestionThree',
  'teamReflectionQuestionFour',
  'teamReflectionQuestionFive',
  'teamReflectionQuestionSix',
  'teamReflectionQuestionSeven',
  'teamReflectionQuestionEight',
  'teamReflectionQuestionNine',
  'teamReflectionQuestionTen',
  'teamReflectionQuestionEleven'
] as const

export interface StarChartVisualizationProps {
  /**
   * Array of normalized scores (0-1) for each behavior
   */
  values: number[]
  /**
   * Optional array of behavior labels (defaults to teamReflectionQuestionOne-Six)
   */
  labels?: string[]
  /**
   * Title for the chart
   */
  title?: string
  /**
   * The amber glow; turned off for a card that already sits inside another card
   */
  hasAccentGlow?: boolean
}

/**
 * StarChartVisualization Component
 *
 * Displays psychological safety behaviors with star ratings.
 * Each behavior gets 1-5 stars based on normalized score (0-1).
 *
 * Design: Glassmorphism card with golden filled stars and dark empty stars
 *
 * @example
 * ```tsx
 * <StarChartVisualization
 *   values={[0.85, 0.70, 0.60, 0.90, 0.65, 0.75]}
 *   labels={[
 *     'teamReflectionQuestionOne',
 *     'teamReflectionQuestionTwo',
 *     'teamReflectionQuestionThree',
 *     'teamReflectionQuestionFour',
 *     'teamReflectionQuestionFive',
 *     'teamReflectionQuestionSix'
 *   ]}
 * />
 * ```
 */
const StarChartVisualization: React.FC<StarChartVisualizationProps> = ({
  values,
  labels,
  title,
  hasAccentGlow = true
}) => {
  const { t } = useTranslation()

  const baseLabels = labels && labels.length > 0 ? labels : [...DEFAULT_TEAM_LABELS]
  const maxItems = Math.min(values.length, baseLabels.length)
  const trimmedLabels = baseLabels.slice(0, maxItems)
  const trimmedValues = values.slice(0, maxItems)

  const translateBehavior = (behaviorKey: string): string => {
    if (!behaviorKey) {
      return t('common.notAvailable', 'Not available')
    }

    return getPsychologicalSafetyQuestionLabel(behaviorKey, t)
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        padding: { xs: '20px', sm: '24px', md: '20px', lg: '16px' },
        gap: { xs: '12px', sm: '16px', md: '12px', lg: '10px' },
        width: '100%',
        minHeight: '222px',
        position: 'relative',
        isolation: 'isolate',
        background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
        backgroundBlendMode: 'soft-light, normal',
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Orange glow ellipse - top right */}
      {hasAccentGlow && (
        <Box
          sx={{
            position: 'absolute',
            width: '244px',
            height: '177px',
            right: '-120px',
            top: '-80px',
            background: INTERAX_COLORS.sun,
            filter: 'blur(75px)',
            zIndex: 0
          }}
        />
      )}

      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Lexend Deca',
          fontWeight: 600,
          fontSize: { xs: '18px', sm: '20px', md: '18px', lg: '16px' },
          lineHeight: { xs: '26px', sm: '28px', md: '24px', lg: '22px' },
          color: INTERAX_COLORS.white,
          marginBottom: { xs: '6px', sm: '8px', md: '6px', lg: '4px' },
          zIndex: 1
        }}
      >
        {title || t('gamer.feedback.psychologicalSafety.title', 'Psychological safety')}
      </Typography>

      {/* Column Headers */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: { xs: '6px', sm: '8px', md: '6px', lg: '4px' },
          borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
          zIndex: 1
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '18px',
            color: INTERAX_COLORS.thunderGrey
          }}
        >
          {t('gamer.feedback.psychologicalSafety.behaviour', 'Behaviour')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '18px',
            color: INTERAX_COLORS.thunderGrey
          }}
        >
          {t('gamer.feedback.psychologicalSafety.score', 'Score')}
        </Typography>
      </Box>

      {/* Behavior Rows */}
      {trimmedValues.map((score, index) => {
        const behaviorKey = trimmedLabels[index] || ''
        const starCount = toFivePointScore(score)

        return (
          <Box
            key={behaviorKey}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingY: { xs: '6px', sm: '8px', md: '6px', lg: '4px' },
              zIndex: 1
            }}
          >
            {/* Behavior Label */}
            <Typography
              sx={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '18px',
                color: INTERAX_COLORS.white,
                flex: 1
              }}
            >
              {translateBehavior(behaviorKey)}
            </Typography>

            {/* Star Rating */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: { xs: '2px', sm: '2px', md: '1px', lg: '1px' },
                alignItems: 'center'
              }}
            >
              {Array.from({ length: 5 }).map((_, starIndex) => {
                const isFilled = starIndex < starCount

                return (
                  <Box
                    key={starIndex}
                    sx={{
                      width: { xs: '28px', sm: '33px', md: '28px', lg: '24px' },
                      height: { xs: '28px', sm: '33px', md: '28px', lg: '24px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    {isFilled ? (
                      <Box
                        sx={{
                          width: { xs: '33px', md: '28px', lg: '24px' },
                          height: { xs: '33px', md: '28px', lg: '24px' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
                          border: '0.214286px solid rgba(255, 255, 255, 0.25)',
                          boxShadow: '0px 0px 2.14286px rgba(0, 0, 0, 0.5), 0px 0px 4.28571px rgba(255, 171, 64, 0.4)',
                          borderRadius: '4px',
                          clipPath:
                            'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: '33px', md: '28px', lg: '24px' },
                          height: { xs: '33px', md: '28px', lg: '24px' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(33, 38, 41, 0.4)',
                          boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
                          borderRadius: '4px',
                          clipPath:
                            'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                        }}
                      />
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default StarChartVisualization
